import { Box, Text, VStack } from "@biblioteksentralen/react";
import { getSiteUrl, numberToWord } from "@libry-content/common";
import { FrontendLocale, dateFnsLocaleMap } from "@libry-content/localization";
import { AdventCalendarDoor } from "@libry-content/types";
import formatDuration from "date-fns/formatDuration";
import intervalToDuration from "date-fns/intervalToDuration";
import { useEffect } from "react";
import { logEvent } from "../../../utils/analytics/Plausible";
import { TranslateMicroText, useTranslation } from "../../../utils/hooks/useTranslation";
import { useCommonData } from "../../layout/CommonDataProvider";
import { ShareToSocialMedia } from "../SocialMedia/ShareToSocialMedia";
import { CorrectSolutionAvatar } from "./CorrectSolutionAvatar";
import { CoverImageGrid } from "./GuessAuthor/CoverImageGrid";
import { setupGuessStates } from "./GuessAuthor/GuessAuthor";
import { WrongSolutionAvatar } from "./WrongSolutionAvatar";
import { Answer } from "./types";

const formatSolvedTime = (languageCode: FrontendLocale, time: number): string =>
  formatDuration(intervalToDuration({ start: 0, end: time * 1000 }), {
    locale: dateFnsLocaleMap[languageCode],
    delimiter: " og ",
  });

type Props = {
  answered: Answer;
  date: number;
  solvedTime?: number;
  attempts?: number;
  door: AdventCalendarDoor;
};

const getText = (
  t: TranslateMicroText,
  languageCode: FrontendLocale,
  { door, answered, solvedTime, attempts }: Props
): string => {
  switch (door?._type) {
    case "adventCalendarDoorQuizWithAlternatives":
      return answered === "correct" ? t("Riktig! Gratulerer!") : t("Uffda! Det var feil.");
    case "adventCalendarDoorWordSearch":
      return solvedTime
        ? t("Gratulerer! Du klarte ordjakten på {tid}", { tid: formatSolvedTime(languageCode, solvedTime) })
        : t("Gratulerer, du klarte ordjakten!");
    case "adventCalendarDoorGuessAuthor":
      return attempts
        ? attempts === 1
          ? t("Gratulerer! Du klarte det på første forsøk")
          : t("Gratulerer! Du klarte det på {antall} forsøk", { antall: numberToWord(languageCode, attempts) })
        : t("Gratulerer, du klarte det!");
    case "adventCalendarDoorQuote":
    case "adventCalendarDoorVideo":
      console.error(`Attempting to show solution page for calendar door type "${door?._type}"`);
      return "";
  }
};

export const Solution = (props: Props) => {
  const { answered, date, door } = props;
  const { t, lang } = useTranslation();
  const { site } = useCommonData();

  const shareUrl = site ? `${getSiteUrl(site)}/kampanje/julekalender` : undefined;

  useEffect(() => {
    logEvent("AdventCalendarCompleted", { caledarDoorType: door?._type, date });
  }, [date, door?._type]);

  return (
    <VStack alignItems="center" justifyContent="center" gap="1rem" textAlign="center" fontWeight={600} margin="auto">
      <Text fontSize="xl" padding="0 1rem">
        {getText(t, lang, props)}
      </Text>
      {door._type === "adventCalendarDoorGuessAuthor" && (
        <Box minWidth={{ base: undefined, sm: "20rem" }}>
          <CoverImageGrid
            publications={door.publications}
            guessStates={setupGuessStates(door.publications?.length ?? 0, "correct")}
          />
        </Box>
      )}
      {door._type !== "adventCalendarDoorGuessAuthor" && (
        <Box maxH="20rem">{answered === "correct" ? <CorrectSolutionAvatar /> : <WrongSolutionAvatar />}</Box>
      )}
      <Text fontSize="xl" maxWidth="14rem">
        {answered === "correct"
          ? t("Kom tilbake i morgen for en ny luke")
          : t("Prøv igjen, eller kom tilbake i morgen for en ny luke")}
      </Text>
      {shareUrl && <ShareToSocialMedia shareUrl={shareUrl} answered={answered} type={door._type} date={date} />}
    </VStack>
  );
};
